import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  DatePicker,
  Layout,
  Modal,
  Select,
  Spin,
  Typography,
} from "antd";

import { formatDateTime } from "../../utils/appUtils";
import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import ImageCardDisplay from "../../components/common/ImageCardDisplay";
import "../../assets/styles/components_styles/tickets.css";
import {
  fetchUserBookings,
  selectUserBookings,
  selectFetchUserBookingsLoading,
  selectFetchUserBookingsError,
  cancelUserBooking
} from "../../redux/ticketSlice";

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DATE_FMT = "YYYY-MM-DD";

const INITIAL_VISIBLE_CARDS = 6;
const LOAD_MORE_CARDS = 3;

const SORT_BY = {
  ORDER_DATE_DESC: "order_date_desc",
  ORDER_DATE_ASC: "order_date_asc",
  NAME_ASC: "name_asc",
  NAME_DESC: "name_desc",
};

const SORT_OPTIONS = [
  { value: SORT_BY.ORDER_DATE_DESC, label: "Order date (newest first)" },
  { value: SORT_BY.ORDER_DATE_ASC, label: "Order date (oldest first)" },
  { value: SORT_BY.NAME_ASC, label: "Event name (A–Z)" },
  { value: SORT_BY.NAME_DESC, label: "Event name (Z–A)" },
];

/** Formats booking total with currency (uses API code when present, defaults to INR). */
function formatBookingTotal(booking) {
  const raw = booking?.total_amount;
  const n = typeof raw === "number" ? raw : parseFloat(String(raw ?? "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "—";
  const code =
    booking?.currency?.currency_code ||
    booking?.tickets?.[0]?.currency?.currency_code ||
    "INR";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `₹${n.toLocaleString("en-IN")}`;
  }
}

function isEventStartInFuture(booking) {
  const raw = booking?.event?.event_start_time;
  if (!raw) return false;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return false;
  return t > Date.now();
}

function canShowCancelBooking(booking) {
  return Boolean(booking?.event?.is_refundable) && isEventStartInFuture(booking);
}

export default function TicketsPage() {
  const dispatch = useDispatch();
  const userBookings = useSelector(selectUserBookings);
  const fetchUserBookingsLoading = useSelector(selectFetchUserBookingsLoading);
  const fetchUserBookingsError = useSelector(selectFetchUserBookingsError);
  const [sortBy, setSortBy] = useState(SORT_BY.ORDER_DATE_DESC);
  const [eventDateRange, setEventDateRange] = useState(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_CARDS);
  const [cancelModalBooking, setCancelModalBooking] = useState(null);

  /** Cancel User Booking State */
  const cancelUserBookingSuccess = useSelector(state => state.ticket.cancelUserBookingSuccess);
  const cancelUserBookingData = useSelector(state => state.ticket.cancelUserBookingData);

  const closeCancelBookingModal = () => setCancelModalBooking(null);

  const handleCancelBookingContinue = (booking) => {
    const payload = {
      order_id: booking.order_id,
      payment_id: booking.payment_id,
      event_id: booking.event.id,
      user_id: booking.user.id,
      amount: booking.total_amount,
      currency: booking.currency ?? "INR",
    };

    dispatch(cancelUserBooking(payload));
  };
  
  useEffect(() => {
    if (cancelUserBookingSuccess) {
      console.log("cancelUserBookingData ::: ", cancelUserBookingData);
      closeCancelBookingModal();
    }
  }, [cancelUserBookingSuccess]);

  useEffect(() => {
    dispatch(fetchUserBookings());
  }, [dispatch]);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_CARDS);
  }, [eventDateRange, sortBy]);

  const filteredBookings = useMemo(() => {
    const list = [...(userBookings || [])];
    if (!eventDateRange?.[0] || !eventDateRange?.[1]) return list;
    const startMs = eventDateRange[0].startOf("day").valueOf();
    const endMs = eventDateRange[1].endOf("day").valueOf();
    return list.filter((b) => {
      const raw = b?.event?.event_start_time;
      if (!raw) return false;
      const t = new Date(raw).getTime();
      if (Number.isNaN(t)) return false;
      return t >= startMs && t <= endMs;
    });
  }, [userBookings, eventDateRange]);

  const sortedBookings = useMemo(() => {
    const orderTime = (booking) => {
      const t = booking?.created_at ? new Date(booking.created_at).getTime() : 0;
      return Number.isNaN(t) ? 0 : t;
    };
    const eventName = (booking) =>
      String(booking?.event?.event_name ?? "").toLowerCase();

    const list = [...filteredBookings];
    switch (sortBy) {
      case SORT_BY.ORDER_DATE_ASC:
        return list.sort((a, b) => orderTime(a) - orderTime(b));
      case SORT_BY.NAME_ASC:
        return list.sort((a, b) => eventName(a).localeCompare(eventName(b)));
      case SORT_BY.NAME_DESC:
        return list.sort((a, b) => eventName(b).localeCompare(eventName(a)));
      case SORT_BY.ORDER_DATE_DESC:
      default:
        return list.sort((a, b) => orderTime(b) - orderTime(a));
    }
  }, [filteredBookings, sortBy]);

  const visibleBookings = useMemo(
    () => sortedBookings.slice(0, visibleCount),
    [sortedBookings, visibleCount]
  );
  const hasMoreBookings = visibleCount < sortedBookings.length;

  const totalBookingsCount = userBookings?.length ?? 0;
  const hasBookings = sortedBookings.length > 0;
  const hasFilterButNoMatches = totalBookingsCount > 0 && !hasBookings;
  const showFilters =
    !fetchUserBookingsLoading &&
    !fetchUserBookingsError &&
    totalBookingsCount > 0;

  // ========== RENDER ==========
  return (
    <>
      {/* SEO HELMET WRAPPER */}
      <HelmetWrapper
          title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai" 
          description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
          and city-specific visibility for organizers and users." 
          keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
          online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
          Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
          Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
      />
      {/* HEADER */}
      <Header isHome={false} />
      {/* CONTENT */}
      <Content className="topheaderLayout ticketsPageLayout">
        <div className="ticketsPageContainer">
          <div className="ticketsPageTitleRow">
            <Title level={2} className="ticketsPageTitle">
              Your Bookings &amp; Purchases
            </Title>
            {showFilters ? (
              <div className="ticketsFiltersGroup">
                <RangePicker
                  className="ticketsEventDateRange"
                  popupClassName="mobileDateRangePickerDropdown"
                  value={eventDateRange}
                  onChange={setEventDateRange}
                  format={DATE_FMT}
                  allowClear
                  size="large"
                  placeholder={["Event from", "Event to"]}
                  aria-label="Filter by event date range"
                />
                <Select
                  className="ticketsSortSelect"
                  value={sortBy}
                  onChange={setSortBy}
                  options={SORT_OPTIONS}
                  size="large"
                  aria-label="Sort bookings"
                />
              </div>
            ) : null}
          </div>

          {fetchUserBookingsLoading ? (
            <div className="ticketsLoadingWrap">
              <Spin size="large" tip="Loading your bookings..." />
            </div>
          ) : fetchUserBookingsError ? (
            <div className="ticketsEmptyState">
              <Text type="danger" className="ticketsEmptyTitle">
                {fetchUserBookingsError}
              </Text>
              <Text className="ticketsEmptySubtitle">
                Please try again later.
              </Text>
            </div>
          ) : hasFilterButNoMatches ? (
            <div className="ticketsFilteredEmpty">
              <Text className="ticketsFilteredEmptyTitle">
                No bookings match the selected event date range.
              </Text>
              <Button type="primary" ghost onClick={() => setEventDateRange(null)}>
                Clear event date filter
              </Button>
            </div>
          ) : hasBookings ? (
            <div className="ticketsBookingsGrid">
              {visibleBookings.map((booking, index) => (
                <div
                  key={booking.id != null ? String(booking.id) : `booking-${index}`}
                  className="bookingGroup"
                >
                  <Text className="bookingOrderedOn">
                    Ordered on: <b>{formatDateTime(booking.created_at)}</b>
                  </Text>

                  <article className="ticketCardLite">
                    <div className="ticketCardTop">
                      <ImageCardDisplay cardElement={booking?.event} customClass={"ticketPoster"} />
                      <div className="ticketBody">
                        <div className="ticketTopRow">
                          <Text
                            className="ticketTitle"
                            title={booking?.event?.event_name || undefined}
                          >
                            {booking?.event?.event_name}
                          </Text>
                        </div>

                        <Text className="ticketShowDate">{formatDateTime(booking?.event?.event_start_time)}</Text>
                        <Text className="ticketVenue">
                          {`${booking?.venue?.venue_name}`}
                        </Text>
                        <Text className="ticketVenue">
                          {`${booking?.venue?.city?.name}, ${booking?.venue?.state?.name}, ${booking?.venue?.country?.name}`}
                        </Text>

                        <div className="ticketBottomRow">
                          <Text className="ticketSeats">
                            {booking?.tables?.length > 0
                              ? booking.tables
                                  .map(
                                    (table) =>
                                      `${table.label} — Table #${table.table_number} (${table.persons_count} persons)`
                                  )
                                  .join(" | ")
                              : booking?.tickets?.length > 0
                              ? booking.tickets
                                  .map(
                                    (ticket) =>
                                      `${ticket.ticket_category_name} - ${ticket.no_of_seats}`
                                  )
                                  .join(" | ")
                              : "No booking items"}
                          </Text>
                        </div>
                      </div>
                    </div>

                    <div className="ticketStatusRow">
                      <div className="ticketStatusRowLeft">
                        <span className={`ticketStatusPill ${booking?.booking_status?.toLowerCase()}`}>
                          {booking.booking_status.toUpperCase()}
                        </span>
                        {canShowCancelBooking(booking) ? (
                          <Button
                            type="default"
                            size="small"
                            className="ticketCancelBtn"
                            onClick={() => setCancelModalBooking(booking)}
                          >
                            Cancel
                          </Button>
                        ) : null}
                      </div>
                      <Text className="ticketAmountText">
                        {formatBookingTotal(booking)}
                      </Text>
                    </div>
                  </article>
                </div>
              ))}
              {hasMoreBookings ? (
                <div className="ticketsViewMoreRow">
                  <Button
                    type="primary"
                    size="large"
                    onClick={() =>
                      setVisibleCount((c) => c + LOAD_MORE_CARDS)
                    }
                  >
                    View More
                  </Button>
                </div>
              ) : (
                <p className="ticketsEndNote">You have no more bookings</p>
              )}
            </div>
          ) : (
            <div className="ticketsEmptyState">
              <Text className="ticketsEmptyTitle">No bookings available</Text>
              <Text className="ticketsEmptySubtitle">
                Your ticket bookings and purchases will appear here once you make
                a booking.
              </Text>
            </div>
          )}
        </div>
      </Content>

      <Modal
        title="Cancel booking"
        open={cancelModalBooking != null}
        onCancel={closeCancelBookingModal}
        footer={[
          <Button key="keep" onClick={closeCancelBookingModal}>
            Keep Booking
          </Button>,
          <Button
            key="continue"
            type="primary"
            danger
            onClick={() => handleCancelBookingContinue(cancelModalBooking)}
          >
            Continue
          </Button>
        ]}
        destroyOnClose
        centered
      >
        <Text type="secondary">
          Are you sure you want to cancel this booking? This action cannot be undone from here.
        </Text>
      </Modal>

      {/* FOOTER */}
      <Footer />
    </>
  );
}
